import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

interface FormInput {
  id: string
  unitName: string
  unitKanaName?: string
  address: string
  email: string
  tel: string
  firstName: string
  lastName: string
}
export default function OrganizerRegister() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<FormInput>()
  const schema = yup.object({
    id: yup
      .string()
      .required('IDを入力してください。')
      .min(8, 'IDは8文字以上で入力してください。')
      .matches(
        /^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/,
        'IDは半角英数記号のみで入力してください'
      ),
    unitName: yup.string().required('団体名を入力してください。'),
    address: yup.string().required('所在地を入力してください。'),
    email: yup
      .string()
      .required('メールアドレスを入力してください。')
      .email('メールアドレスの形式が正しくありません。'),
    tel: yup
      .string()
      .required('電話番号を入力してください。')
      .matches(/^0[0-9]{9,10}$/, '電話番号はハイフンなしで入力してください。'),
    firstName: yup.string().required('担当者名を入力してください。'),
    lastName: yup.string().required('担当者姓を入力してください。'),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {},
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: '顧客管理',
          url: '/organizer',
        },
        {
          text: '新規登録',
          url: '/organizer/register',
        },
      ])
    )
  }, [dispatch])
  const onSubmitForm: SubmitHandler<FormInput> = (data) => {
    setData(data)
  }
  const handleRegister = async () => {
    try {
      setLoading(true)
      await axiosClient.post('/api/organizer/register', data)
      enqueueSnackbar('新規作成が完了しました。', {
        variant: 'success',
      })
      navigate('/organizer')
    } catch (error) {
      const res = error as any
      if (res.status === 409) {
        enqueueSnackbar('既にメールアドレスまたはIDが存在しています。', {
          variant: 'error',
        })
      } else {
        enqueueSnackbar('エラーが発生しました。', {
          variant: 'error',
        })
      }
      setData(undefined)
    } finally {
      setLoading(false)
    }
  }
  return (
    <>
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h2" sx={{ borderBottom: 2 }}>
            新規登録
          </Typography>
        </Grid>
        <Grid item xs={12} ml={1}>
          <Container
            disableGutters
            component="form"
            noValidate
            autoComplete="off"
            maxWidth="sm"
            sx={{
              margin: '0',
              padding: '0 !important',
              mt: 2,
            }}
            onSubmit={handleSubmit(onSubmitForm)}
          >
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{ fontWeight: 'bold' }}
            >
              基本情報
            </Typography>
            <TextField
              autoFocus
              required
              label="団体名"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('unitName')}
              error={'unitName' in errors}
              helperText={errors.unitName?.message}
            />
            <TextField
              label="団体名カナ（任意）"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('unitKanaName')}
              error={'unitKanaName' in errors}
              helperText={errors.unitKanaName?.message}
            />
            <TextField
              required
              label="所在地"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('address')}
              error={'address' in errors}
              helperText={errors.address?.message}
            />
            <TextField
              required
              label="メールアドレス"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('email')}
              error={'email' in errors}
              helperText={errors.email?.message}
            />
            <TextField
              required
              label="電話番号"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('tel')}
              error={'tel' in errors}
              helperText={errors.tel?.message}
            />
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{ fontWeight: 'bold', mt: 2 }}
            >
              担当者情報
            </Typography>
            <TextField
              required
              label="ログインID"
              margin="dense"
              fullWidth
              variant="standard"
              {...register('id')}
              error={'id' in errors}
              helperText={errors.id?.message}
            />
            <Stack
              direction="row"
              spacing={2}
              alignItems="baseline"
              justifyContent="space-between"
            >
              <TextField
                required
                label="姓"
                margin="dense"
                fullWidth
                variant="standard"
                {...register('lastName')}
                error={'lastName' in errors}
                helperText={errors.lastName?.message}
              />
              <TextField
                required
                label="名"
                margin="dense"
                fullWidth
                variant="standard"
                {...register('firstName')}
                error={'firstName' in errors}
                helperText={errors.firstName?.message}
              />
            </Stack>
            <Button
              disabled={Object.keys(errors).length > 0}
              color="inherit"
              variant="outlined"
              sx={{
                px: 12,
                borderColor: '#aaa',
                borderRadius: 2,
                mt: 4,
              }}
              type="submit"
            >
              登録
            </Button>
          </Container>
        </Grid>
      </Grid>
      {data && (
        <DialogConfirm
          open={true}
          loading={loading}
          handleClose={() => setData(undefined)}
          handleSubmit={handleRegister}
          title="登録内容の確認"
          primaryButtonText="登録する"
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              団体名
            </Typography>
            <Typography
              variant="body1"
              component="h4"
              sx={{
                flex: 1,
              }}
              className="mb-4 md:mb-0"
            >
              {data.unitName}
            </Typography>
          </Stack>
          {data.unitKanaName && (
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
            >
              <Typography
                variant="body1"
                component="h4"
                sx={{
                  width: 130,
                  fontWeight: 'bold',
                }}
              >
                団体名カナ
              </Typography>
              <Typography
                variant="body1"
                component="h4"
                sx={{
                  flex: 1,
                }}
                className="mb-4 md:mb-0"
              >
                {data.unitKanaName}
              </Typography>
            </Stack>
          )}
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              所在地
            </Typography>
            <Typography
              variant="body1"
              component="h4"
              sx={{
                flex: 1,
              }}
              className="mb-4 md:mb-0"
            >
              {data.address}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              メールアドレス
            </Typography>
            <Typography
              variant="body1"
              component="h4"
              sx={{
                flex: 1,
              }}
              className="mb-4 md:mb-0"
            >
              {data.email}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              電話番号
            </Typography>
            <Typography
              variant="body1"
              component="h4"
              sx={{
                flex: 1,
              }}
              className="mb-4 md:mb-0"
            >
              {data.tel}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              ログインID
            </Typography>
            <Typography variant="body1" component="h4">
              {data.id}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              担当者氏名
            </Typography>
            <Typography variant="body1" component="h4">
              {`${data.lastName} ${data.firstName}`}
            </Typography>
          </Stack>
        </DialogConfirm>
      )}
    </>
  )
}
